import type { FaqItem } from '@models/faq.model';
import type { NutritionTip } from '@models/nutritional-plan.model';

/** App-wide constants. All cookie names, storage keys, navigation identifiers. */
export const TOKEN_NAME = 'MST_TOKEN';
export const REFRESH_TOKEN_NAME = 'MST_REFRESH_TOKEN';

export const DRAFT_TRAINING_ANAMNESIS_STORAGE = 'MST_DRAFT_TRAINING_ANAMNESIS';
export const DRAFT_NUTRITION_ANAMNESIS_STORAGE = 'MST_DRAFT_NUTRITION_ANAMNESIS';
export const GYM_PROGRAM_CURRENT_CARD_INDEX = 'GYM_PROGRAM_CURRENT_CARD_INDEX';
export const APP_DISPLAY_MODE = 'APP_DISPLAY_MODE';
export const APP_DISPLAY_MODE_BROWSER = 'browser';
export const APP_DISPLAY_MODE_STANDALONE = 'standalone';

export const SEX_ID_MALE = 0;
export const SEX_ID_FEMALE = 1;

// ─── get_constants — API response grouping keys ───────────────────────────────
export const ANAMNESIS_STATUSES_KEY = 'ANAMNESIS_STATUSES';
export const TRAINING_PLAN_STATUS_KEY = 'TRAINING_PLAN_STATUS';
export const STANDARD_USER_PLAN_FLAGS_KEY = 'STANDARD_USER_PLAN_FLAGS';
export const GYM_PLAN_STATUSES_KEY = 'GYM_PLAN_STATUSES';
export const NUTRITIONAL_PLAN_STATUS_KEY = 'NUTRITIONAL_PLAN_STATUS';
export const VIDEO_BODY_PARTS_KEY = 'VIDEO_BODY_PARTS';

// ─── Anamnesis status tags ─────────────────────────────────────────────────────
export const ANAMNESIS_DRAFT = 'DRAFT';
export const ANAMNESIS_SENT = 'SENT';
export const ANAMNESIS_APPROVED = 'APPROVED';
export const ANAMNESIS_EXPIRED = 'EXPIRED';

// ─── Training plan status tags ─────────────────────────────────────────────────
export const TRAINING_GENERATED = 'GENERATED';
export const TRAINING_READY_TO_PROCESS = 'READY_TO_PROCESS';
export const TRAINING_DRAFT = 'DRAFT';
export const TRAINING_READY = 'READY';
export const TRAINING_EXPIRED = 'EXPIRED';

// ─── Plan flag tags ────────────────────────────────────────────────────────────
export const PLAN_FLAG_NEW = 'NEW';
export const PLAN_FLAG_RUNNING = 'RUNNING';
export const PLAN_FLAG_RENEWAL = 'RENEWAL';

// ─── Gym plan status tags ──────────────────────────────────────────────────────
export const GYM_QUEUED = 'QUEUED';
export const GYM_READY = 'READY';
export const GYM_EXPIRED = 'EXPIRED';

// ─── Nutritional plan status tags ─────────────────────────────────────────────
export const NUTRITION_GENERATED = 'GENERATED';
export const NUTRITION_READY_TO_PROCESS = 'READY_TO_PROCESS';
export const NUTRITION_AWAITING_NUTRITIONIST = 'AWAITING_NUTRITIONIST';
export const NUTRITION_DRAFT = 'DRAFT';
export const NUTRITION_REFERT_TO_REVIEW = 'REFERT_TO_REVIEW';
export const NUTRITION_READY = 'READY';
export const NUTRITION_COMPLETED = 'COMPLETED';

// ─── Meal type labels and images ───────────────────────────────────────────────────
/** Italian label for each meal type_id returned by the API (uppercase for tag chips). */
export const MEAL_TYPE_LABELS: Record<number, string> = {
    0: 'COLAZIONE',
    1: 'PRANZO',
    2: 'CENA',
    3: 'SNACK',
    4: 'NOTE',
};

/** Title-case label for each meal type_id (used as dialog title). */
export const MEAL_TYPE_TITLES: Record<number, string> = {
    0: 'Colazione',
    1: 'Pranzo',
    2: 'Cena',
    3: 'Snack',
    4: 'Note',
};

/** Background image path for each meal type_id. */
export const MEAL_TYPE_IMAGES: Record<number, string> = {
    0: '/images/graphics/graphic-breakfast-vertical.jpg',
    1: '/images/graphics/graphic-lunch-vertical.jpg',
    2: '/images/graphics/graphic-dinner-vertical.jpg',
    3: '/images/graphics/graphic-snack-vertical.jpg',
    4: '/images/graphics/graphic-note-vertical.jpg',
};

/** Introductory subtitle paragraphs shown on the meal detail page, by type_id. */
export const MEAL_TYPE_SUBTITLES: Record<number, readonly string[]> = {
    0: [
        'Si consiglia di attendere circa 20 min dal risveglio per fare colazione.',
        'Si consiglia di consumare massimo 3 caffè al giorno.',
        'Cappuccino possibilmente solo vegetale, 2 volte a settimana.',
    ],
    1: [
        'Le combinazioni indicate non corrispondono a dei giorni prefissati della settimana; scegli liberamente cosa mangiare in base alle tue voglie e disponibilita; ricordati di variare ogni volta la scelta di verdure, carboidrati, proteine e grassi.',
        '10 ml olio evo per ogni proposta.',
    ],
    2: [
        'Le combinazioni indicate non corrispondono a dei giorni prefissati della settimana; scegli liberamente cosa mangiare in base alle tue voglie e disponibilita; ricordati di variare ogni volta la scelta di verdure, carboidrati, proteine e grassi.',
        '10 ml olio evo per ogni proposta.',
    ],
    3: [
        "Gli spuntini sono importanti; contribuiscono a mantenere attivo il metabolismo e a fornire energia al nostro corpo soprattutto prima o dopo l'attivita fisica.",
        'Se non hai fame non forzarti.',
        'Puoi invertire lo spuntino della mattina con quello del pomeriggio.',
    ],
    4: [],
};

// ─── Nutrition tips and FAQs ───────────────────────────────────────────────────

/** Seven static nutrition tips shown in the tips carousel (from legacy app). */
export const NUTRITION_TIPS: NutritionTip[] = [
    {
        index: 1,
        image: '/images/graphics/graphic-nutrition-01.webp',
        body: 'Le calorie contano, fai caso a quante ne assumi durante la tua giornata.',
    },
    {
        index: 2,
        image: '/images/graphics/graphic-nutrition-02.webp',
        body: 'Mangia molta verdura, preferendo sempre quelle di stagione.',
    },
    {
        index: 3,
        image: '/images/graphics/graphic-nutrition-03.webp',
        body: 'Varia le fonti alimentari il più possibile in modo tale che il tuo corpo riceva input diversi.',
    },
    {
        index: 4,
        image: '/images/graphics/graphic-nutrition-04.webp',
        body: "Inizia la tua giornata con un bel bicchiere d'acqua naturale. Ti farà bene idratarti dopo la notte.",
    },
    {
        index: 5,
        image: '/images/graphics/graphic-nutrition-05.webp',
        body: "Riduci i condimenti superflui. Prediligi spezie e aromi e ricordati che l'olio extravergine di oliva fa bene ma va sempre dosato.",
    },
    {
        index: 6,
        image: '/images/graphics/graphic-nutrition-06.webp',
        body: 'Allenati con costanza per ottimizzare i benefici del tuo programma alimentare.',
    },
    {
        index: 7,
        image: '/images/graphics/graphic-nutrition-07.webp',
        body: 'Attenta agli spuntini non programmati dal tuo piano alimentare. Potrebbero essere letali!!',
    },
];

/** FAQ entries shown in the nutrition dashboard accordion. */
export const NUTRITION_FAQS: readonly FaqItem[] = [
    {
        title: 'Come si sviluppa il percorso nutrition?',
        description:
            'Riceverai una dieta personalizzata e avrai assistenza dalla tua nutrizionista per i primi 5 giorni dal tuo acquisto. Sono compresi nel costo del pacchetto il ricettario, gli e-book sulla cellulite e ricette sempre nuove e aggiornate.',
    },
    {
        title: 'Come viene redatto il piano alimentare senza la visita della nutrizionista?',
        description:
            'Il piano viene elaborato sulla base delle informazioni fornite tramite il questionario anamnestico. La tua nutrizionista analizza le tue risposte e crea un piano su misura per le tue esigenze.',
    },
    {
        title: 'Posso avere la dieta anche se sono vegana/vegetariana/intollerante/celiaca?',
        description:
            'Certamente. Nel questionario anamnestico puoi indicare le tue preferenze e intolleranze alimentari; il piano sarà redatto di conseguenza.',
    },
];

// ─── Nutrition anamnesis wizard steps ─────────────────────────────────────────

/**
 * Nutrition anamnesis wizard step definitions.
 * Each entry combines the intro label (shown in the "Prossimi step" list) with the
 * API question name keys (matched against the `name` field returned by the API).
 */
export const NUTRITION_ANAMNESIS_STEPS: readonly { readonly label: string; readonly questions: readonly string[] }[] = [
    {
        label: 'I tuoi dati personali',
        questions: [
            'ANAMNESIS_QUESTION_BIRTH_DATE_ID',
            'ANAMNESIS_QUESTION_FISCAL_CODE_ID',
            'ANAMNESIS_QUESTION_RESIDENCE_CITY_ID',
            'ANAMNESIS_QUESTION_RESIDENCE_ADDRESS_ID',
            'ANAMNESIS_QUESTION_PHONE_NUMBER_ID',
        ],
    },
    {
        label: 'Fisico e sport',
        questions: [
            'ANAMNESIS_QUESTION_HEIGHT_ID',
            'ANAMNESIS_QUESTION_WEIGHT_ID',
            'ANAMNESIS_QUESTION_SURGERY_ID',
            'ANAMNESIS_QUESTION_SPORT_PLAYER_PAST_ID',
            'ANAMNESIS_QUESTION_SPORT_PAST_LIST_ID',
            'ANAMNESIS_QUESTION_SPORT_PAST_PLAY_FREQUENCY_ID',
            'ANAMNESIS_QUESTION_PAST_TRAINING_ID',
            'ANAMNESIS_QUESTION_LEGS_PROBLEMS_ID',
            'ANAMNESIS_QUESTION_CELLULITE_ID',
            'ANAMNESIS_QUESTION_MOONY_MOOD_ID',
            'ANAMNESIS_QUESTION_SELFESTEEM_ID',
            'ANAMNESIS_QUESTION_OBJECTIVES_ID',
        ],
    },
    {
        label: 'Stile di vita',
        questions: [
            'ANAMNESIS_QUESTION_WEIGHT_HISTORY_ID',
            'ANAMNESIS_QUESTION_PAST_DIETS_ID',
            'ANAMNESIS_QUESTION_SLEEPING_QUALITY_ID',
            'ANAMNESIS_QUESTION_SLEEPING_HOURS_ID',
            'ANAMNESIS_QUESTION_ARTICOLAR_PAINS_ID',
            'ANAMNESIS_QUESTION_WORK_ID',
            'ANAMNESIS_QUESTION_WORKING_HOURS_ID',
            'ANAMNESIS_QUESTION_PHISICAL_ACTIVITY_ID',
            'ANAMNESIS_QUESTION_PHISICAL_ACTIVITY_FREQUENCY_ID',
            'ANAMNESIS_QUESTION_PHISICAL_ACTIVITY_HOURS_ID',
            'ANAMNESIS_QUESTION_SMOKE_NUTR_ID',
            'ANAMNESIS_QUESTION_ALCOHOL_NUTR_ID',
            'ANAMNESIS_QUESTION_WATER_ID',
            'ANAMNESIS_QUESTION_DIURESIS_ID',
            'ANAMNESIS_QUESTION_INTESTIN_ID',
            'ANAMNESIS_QUESTION_INTESTIN_FREQUENCY_ID',
            'ANAMNESIS_QUESTION_MENSTRUAL_CYCLE_ID',
            'ANAMNESIS_QUESTION_PAST_PREGNANCIES_ID',
            'ANAMNESIS_QUESTION_PREGNANT_OR_BREASTFEEDING_ID',
            'ANAMNESIS_QUESTION_CONTRACEPTIVE_ID',
            'ANAMNESIS_QUESTION_CONTRACEPTIVE_WHICH_ID',
            'ANAMNESIS_QUESTION_CONTRACEPTIVE_SINCE_WHEN_ID',
        ],
    },
    {
        label: 'Salute e patologie',
        questions: [
            'ANAMNESIS_QUESTION_PAST_PATHOLOGIES_ID',
            'ANAMNESIS_QUESTION_PRESENT_PATHOLOGIES_ID',
            'ANAMNESIS_QUESTION_ANTICOAGULANT_ID',
            'ANAMNESIS_QUESTION_DIABETES_ID',
            'ANAMNESIS_QUESTION_PHARMACEUTICALS_NUTR_ID',
            'ANAMNESIS_QUESTION_ALLERGIES_OR_INTOLERANCES_ID',
        ],
    },
    {
        label: 'Abitudini alimentari',
        questions: [
            'ANAMNESIS_QUESTION_ALIMENTARY_REGYME_ID',
            'ANAMNESIS_QUESTION_EATING_AT_NIGHT_ID',
            'ANAMNESIS_QUESTION_EATING_BETWEEN_MEALS_ID',
            'ANAMNESIS_QUESTION_SUDDEN_HUNGER_ID',
            'ANAMNESIS_QUESTION_MEAT_FISH_EGGS_DAIRY_DISLIKE_ID',
            'ANAMNESIS_QUESTION_VEGETABLES_DISLIKE_ID',
            'ANAMNESIS_QUESTION_FRUITS_DISLIKE_ID',
            'ANAMNESIS_QUESTION_BREAKFAST_ID',
            'ANAMNESIS_QUESTION_MORNING_SNACK_ID',
            'ANAMNESIS_QUESTION_LUNCH_ID',
            'ANAMNESIS_QUESTION_AFTERNOON_SNACK_ID',
            'ANAMNESIS_QUESTION_DINNER_ID',
            'ANAMNESIS_QUESTION_FOOD_OR_SNACKS_CANT_AVOID_ID',
            'ANAMNESIS_QUESTION_DECLARATION_1_ID',
            'ANAMNESIS_QUESTION_DECLARATION_2_ID',
        ],
    },
    {
        label: 'Caricamento foto a figura intera',
        questions: [],
    },
];

// ─── Nutrition review wizard steps ────────────────────────────────────────────

/**
 * Nutrition plan review wizard step definitions.
 * Each entry combines the intro label with the API question name keys.
 * The last step (photo upload) has no API questions.
 */
export const NUTRITION_REVIEW_STEPS: readonly { readonly label: string; readonly questions: readonly string[] }[] = [
    {
        label: 'Come va il piano?',
        questions: [
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_PERCENTAGE_SUCCESSFULLY_FOLLOW_FACTOR_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_FOOD_WEIGHTED_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_FOOD_VARIETY_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_SHOPPING_DIFFICULTIES_ID',
        ],
    },
    {
        label: 'Pasti e sintomi',
        questions: [
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_BREAKFAST_FEELINGS_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_SNACKS_FEELINGS_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_MAIN_MEALS_FEELINGS_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_MEALS_CHANGINGS_ID',
        ],
    },
    {
        label: 'I tuoi progressi',
        questions: [
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_CONSTANCY_WITHIN_PLAN_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_WEIGHT_CHANGE_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_CIRCLES_CHANGE_ID',
        ],
    },
    {
        label: 'Supporto e motivazione',
        questions: [
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_ASSISTANCE_RECEIVED_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_DIFFICULT_RELATIONSHIP_WITH_NUTRITIONIST_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_NEW_PLAN_DIFFICULTIES_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_CUSTOMER_MOTIVATION_ID',
        ],
    },
    {
        label: 'Aggiornamento dati (opzionale)',
        questions: [
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_UPDATED_WEIGHT_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_HEIGHT_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_WORK_ACTIVITY_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_PHYSICAL_ACTIVITY_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_ALLERGIES_INTOLERANCES_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_CURRENT_PATHOLOGIES_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_UNDESIRED_FOOD_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_FAVOURITE_FOOD_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_SPECIAL_NEEDS_ID',
            'NUTRITIONAL_PLAN_REVIEW_QUESTION_EXPECTATIONS_ID',
        ],
    },
    {
        label: 'Caricamento foto',
        questions: [],
    },
];

// ─── Video effort level labels ───────────────────────────────────────────────────

/**
 * Maps `effort_level_id` (0–4) to the Italian display label for premium videos.
 * Id 0 means no level set — returns empty string (not displayed).
 * Labels mirror the `effort_level.tag` values returned by the free-videos API.
 */
export const EFFORT_LEVEL_LABELS: Record<number, string> = {
    1: 'Intensità bassa',
    2: 'Intensità media',
    3: 'Intensità alta',
    4: 'Intensità massima',
};

// ─── Progress constants API keys ───────────────────────────────────────────────
export const USER_PROGRESS_PICTURE_TYPES_KEY = 'USER_PROGRESS_PICTURE_TYPES';
export const USER_PROGRESS_MEASURE_TYPES_KEY = 'USER_PROGRESS_MEASURE_TYPES';

// ─── Progress picture type tags ────────────────────────────────────────────────
export const PROGRESS_PICTURE_FRONTAL_BEFORE = 'FRONTAL_BEFORE';
export const PROGRESS_PICTURE_FRONTAL_AFTER = 'FRONTAL_AFTER';
export const PROGRESS_PICTURE_BACK_BEFORE = 'BACK_BEFORE';
export const PROGRESS_PICTURE_BACK_AFTER = 'BACK_AFTER';
export const PROGRESS_PICTURE_SIDE_BEFORE = 'SIDE_BEFORE';
export const PROGRESS_PICTURE_SIDE_AFTER = 'SIDE_AFTER';

// ─── Progress measure type tags ────────────────────────────────────────────────
export const PROGRESS_MEASURE_HIPS = 'HIPS';
export const PROGRESS_MEASURE_HIPS_BEFORE = 'HIPS_BEFORE';
export const PROGRESS_MEASURE_HIPS_AFTER = 'HIPS_AFTER';
export const PROGRESS_MEASURE_WAISTLINE = 'WAISTLINE';
export const PROGRESS_MEASURE_WAISTLINE_BEFORE = 'WAISTLINE_BEFORE';
export const PROGRESS_MEASURE_WAISTLINE_AFTER = 'WAISTLINE_AFTER';
export const PROGRESS_MEASURE_THIGHS = 'THIGHS';
export const PROGRESS_MEASURE_THIGHS_BEFORE = 'THIGHS_BEFORE';
export const PROGRESS_MEASURE_THIGHS_AFTER = 'THIGHS_AFTER';
export const PROGRESS_MEASURE_WEIGHT = 'WEIGHT';
export const PROGRESS_MEASURE_WEIGHT_BEFORE = 'WEIGHT_BEFORE';
export const PROGRESS_MEASURE_WEIGHT_AFTER = 'WEIGHT_AFTER';

// ─── Progress measure display metadata ────────────────────────────────────────

/** Short display name for each measure key (used as section header). */
export const MEASURE_NAMES: Record<string, string> = {
    HIPS: 'Fianchi',
    WAISTLINE: 'Giro vita',
    THIGHS: 'Cosce',
    WEIGHT: 'Peso',
};

/** Instructional description for each measure key. */
export const MEASURE_DESCRIPTIONS: Record<string, string> = {
    HIPS: 'Misura la circonferenza dei fianchi nel punto più largo.',
    WAISTLINE: 'Misura la circonferenza del girovita nel punto più stretto.',
    THIGHS: 'Misura la circonferenza della coscia nella parte più larga.',
    WEIGHT: 'Sali sulla bilancia e registra il tuo peso.',
};

/** Input field label for each measure key. */
export const MEASURE_INPUT_LABELS: Record<string, string> = {
    HIPS: 'Misura fianchi',
    WAISTLINE: 'Misura girovita',
    THIGHS: 'Misura cosce',
    WEIGHT: 'Inserisci peso',
};

/** Example placeholder value for each measure key. */
export const MEASURE_PLACEHOLDERS: Record<string, string> = {
    HIPS: 'es. 95',
    WAISTLINE: 'es. 72',
    THIGHS: 'es. 56',
    WEIGHT: 'es. 62',
};

/** Aria label for each measure key. */
export const MEASURE_ARIA_LABELS: Record<string, string> = {
    HIPS: 'Fianchi in centimetri',
    WAISTLINE: 'Giro vita in centimetri',
    THIGHS: 'Cosce in centimetri',
    WEIGHT: 'Peso in chilogrammi',
};

/** Italian ordinal names for training plan months (index 0 = month 1). */
const MONTH_ORDINALS = [
    'Primo',
    'Secondo',
    'Terzo',
    'Quarto',
    'Quinto',
    'Sesto',
    'Settimo',
    'Ottavo',
    'Nono',
    'Decimo',
    'Undicesimo',
    'Dodicesimo',
];

/**
 * Grouped constants object for convenient template access.
 * Expose as `protected readonly C = Constants` in the component,
 * then use `@case (C.ANAMNESIS_DRAFT)` in templates.
 */
export const Constants = {
    ANAMNESIS_DRAFT,
    ANAMNESIS_SENT,
    ANAMNESIS_APPROVED,
    ANAMNESIS_EXPIRED,
    TRAINING_GENERATED,
    TRAINING_READY_TO_PROCESS,
    TRAINING_DRAFT,
    TRAINING_READY,
    TRAINING_EXPIRED,
    PLAN_FLAG_NEW,
    PLAN_FLAG_RUNNING,
    PLAN_FLAG_RENEWAL,
    GYM_QUEUED,
    GYM_READY,
    GYM_EXPIRED,
    NUTRITION_GENERATED,
    NUTRITION_READY_TO_PROCESS,
    NUTRITION_AWAITING_NUTRITIONIST,
    NUTRITION_DRAFT,
    NUTRITION_REFERT_TO_REVIEW,
    NUTRITION_READY,
    NUTRITION_COMPLETED,
    USER_PROGRESS_PICTURE_TYPES_KEY,
    USER_PROGRESS_MEASURE_TYPES_KEY,
    PROGRESS_PICTURE_FRONTAL_BEFORE,
    PROGRESS_PICTURE_FRONTAL_AFTER,
    PROGRESS_PICTURE_BACK_BEFORE,
    PROGRESS_PICTURE_BACK_AFTER,
    PROGRESS_PICTURE_SIDE_BEFORE,
    PROGRESS_PICTURE_SIDE_AFTER,
    PROGRESS_MEASURE_HIPS,
    PROGRESS_MEASURE_HIPS_BEFORE,
    PROGRESS_MEASURE_HIPS_AFTER,
    PROGRESS_MEASURE_WAISTLINE,
    PROGRESS_MEASURE_WAISTLINE_BEFORE,
    PROGRESS_MEASURE_WAISTLINE_AFTER,
    PROGRESS_MEASURE_THIGHS,
    PROGRESS_MEASURE_THIGHS_BEFORE,
    PROGRESS_MEASURE_THIGHS_AFTER,
    PROGRESS_MEASURE_WEIGHT,
    PROGRESS_MEASURE_WEIGHT_BEFORE,
    PROGRESS_MEASURE_WEIGHT_AFTER,
    MEASURE_NAMES,
    MEASURE_DESCRIPTIONS,
    MEASURE_INPUT_LABELS,
    MEASURE_PLACEHOLDERS,
    MEASURE_ARIA_LABELS,
    MONTH_ORDINALS,
    EFFORT_LEVEL_LABELS,
} as const;
